/**
 * Service for handling Local Operating Unit (LOU) data
 */

export interface LOU {
    name: string;
    prefix: string;
    sponsor?: string;
    sponsor_country: string;
    code?: string;
    status?: string;
    email?: string;
    website?: string;
    telephone?: string;
    download_url?: string;
    ip_licence_url?: string;
    explicitly_ip_free?: string;
    open_download?: string;
    lei?: string;
}

class LOUService {
    private lous: LOU[] | null = null;
    private loading = false;

    /**
     * Load LOU data from YAML file
     */
    async loadLOUs(): Promise<LOU[]> {
        if (this.lous) {
            return this.lous;
        }

        if (this.loading) {
            // Wait for existing load to complete
            while (this.loading) {
                await new Promise((resolve) => setTimeout(resolve, 100));
            }
            return this.lous || [];
        }

        this.loading = true;

        try {
            const response = await fetch('/data/lous.yaml');
            if (!response.ok) {
                throw new Error(`Failed to load LOU data: ${response.statusText}`);
            }

            const yamlText = await response.text();

            // Parse YAML manually since it's a simple structure
            this.lous = this.parseYAML(yamlText);

            return this.lous;
        } catch (error) {
            console.error('Error loading LOU data:', error);
            return [];
        } finally {
            this.loading = false;
        }
    }

    /**
     * Simple YAML parser for the LOU data structure
     */
    private parseYAML(yamlText: string): LOU[] {
        const lous: LOU[] = [];
        const lines = yamlText.split('\n');
        let currentLOU: Partial<LOU> = {};

        for (const line of lines) {
            const trimmed = line.trim();

            if (trimmed === '---' || trimmed === '') {
                continue;
            }

            if (trimmed.startsWith('- :name:')) {
                // Start of new LOU entry
                if (currentLOU.name) {
                    lous.push(currentLOU as LOU);
                }
                currentLOU = {};
                const nameMatch = trimmed.match(/- :name:\s*"?([^"]*)"?/);
                if (nameMatch) {
                    currentLOU.name = nameMatch[1].replace(/"/g, '');
                }
            } else if (trimmed.startsWith(':')) {
                // Property line
                const match = trimmed.match(/:(\w+):\s*"?([^"]*)"?/);
                if (match) {
                    const [, key, value] = match;
                    if (value && value !== 'null') {
                        (currentLOU as Record<string, string>)[key] = value.replace(/"/g, '');
                    }
                }
            }
        }

        // Add the last LOU
        if (currentLOU.name) {
            lous.push(currentLOU as LOU);
        }

        return lous;
    }

    /**
     * Find LOU by prefix (used in LEI codes)
     */
    async findLOUByPrefix(prefix: string): Promise<LOU | null> {
        const lous = await this.loadLOUs();
        return lous.find((lou) => lou.prefix === prefix) || null;
    }

    /**
     * Find LOU by LEI code prefix (first 4 digits)
     */
    async findLOUByLEI(lei: string): Promise<LOU | null> {
        if (lei.length < 4) return null;
        const prefix = lei.substring(0, 4);
        return this.findLOUByPrefix(prefix);
    }

    /**
     * Get all LOUs
     */
    async getAllLOUs(): Promise<LOU[]> {
        return this.loadLOUs();
    }

    /**
     * Get LOU by exact name match
     */
    async findLOUByName(name: string): Promise<LOU | null> {
        const lous = await this.loadLOUs();
        return lous.find((lou) => lou.name.toLowerCase() === name.toLowerCase()) || null;
    }

    /**
     * Find LOU by the LOU's own LEI code (the lou field in records)
     */
    async findLOUByLOUCode(louCode: string): Promise<LOU | null> {
        const lous = await this.loadLOUs();
        return lous.find((lou) => lou.lei === louCode) || null;
    }
}

export const louService = new LOUService();
